import React, { useState } from "react";
import { Route, Loader2, CheckCircle2, Clock, Car, MapPin, Building2, Globe, X, LocateFixed, ShieldCheck, RotateCcw, ChevronDown, ChevronRight, AlertTriangle, Search } from "lucide-react";
import { planTodayApi, tripSuggestionsApi, apiErrorMessage } from "@/api/client";
import { getCurrentPosition } from "@/native/capacitor";
import { ProspectDrawer } from "@/components/ProspectDrawer";
import { useAuth } from "@/context/AuthContext";
import { refreshVenues, RefreshResult } from "@/components/RefreshVenues";
import { toast } from "sonner";

const CHIP = "inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold";
const fmt = (iso) => (iso ? new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : "");
const DURATIONS = [{ m: 60, l: "1h" }, { m: 120, l: "2h" }, { m: 180, l: "3h" }, { m: 240, l: "4h" }];

const _stage = (f, name) => (f?.stages || []).find((s) => s.name === name) || {};

// Human-readable "why not more?" lines from the deterministic candidate funnel.
function funnelLines(f) {
  if (!f) return [];
  const lines = [];
  const crm = _stage(f, "cached_crm_in_radius").count || 0;
  const fsa = _stage(f, "cached_fsa_in_radius").count || 0;
  lines.push(`Searched within ${f.final_radius_km}km (started at ${f.initial_radius_km}km${f.radius_expansions ? `, expanded ${f.radius_expansions}×` : ""}).`);
  lines.push(`Found ${crm} CRM lead(s) and ${fsa} cached FSA venue(s) in range.`);
  const live = _stage(f, "live_fsa_attempted");
  if (live.bool) lines.push(`Live FSA search ran: ${_stage(f, "live_fsa_added").count || 0} added.`);
  else lines.push(`Live FSA search skipped (${(live.reason_if_skipped || "not needed").replace(/_/g, " ")}).`);
  const ch = _stage(f, "after_channel_filter").dropped_reasons || {};
  const chTotal = Object.values(ch).reduce((a, b) => a + b, 0);
  const fh = _stage(f, "after_fhrs_filter").dropped_reasons || {};
  const removed = chTotal + (fh.excluded_rating || 0) + (fh.no_rating || 0);
  lines.push(`Removed by filters: ${removed} (channel ${chTotal}; FHRS excluded ${fh.excluded_rating || 0}).`);
  const fit = _stage(f, "after_capacity_fit");
  lines.push(`After fitting the time budget: ${fit.final_stops ?? fit.count ?? 0} stop(s).`);
  const pd = f.pool_distribution?.by_fhrs;
  if (pd) {
    lines.push(`Pool FHRS mix: ${pd["5"] || 0} of rating 5, ${pd["4"] || 0} of rating 4, ${pd["3"] || 0} of rating 3, ${pd["null"] || 0} unrated.`);
  }
  if (f.fsa_total_records_fetched) {
    lines.push(`Live FSA: ${f.fsa_total_records_fetched} record(s) across ${f.fsa_pages_fetched} page(s)${f.discovery_truncated ? " (truncated)" : ""}.`);
  }
  if (f.cache_stale || f.stale_refresh) {
    const s = { ran: "refreshed live", rate_limited: "refresh skipped (rate limit)", kill_switch: "refresh skipped (live FSA off)", timeout: "refresh timed out", error: "refresh failed" }[f.stale_refresh] || "not refreshed";
    lines.push(`Venue cache older than 14 days: ${s}.`);
  }
  return lines;
}

function fsaBannerText(state) {
  if (!state) return null;
  if (state.last_result === "disabled") return "Live FSA search is turned off (TRIP_SUG_LIVE_FSA). Showing cached results only.";
  if (state.rate_limited) return "Live FSA search rate limit reached. Showing cached results only — try again shortly.";
  if (state.last_result === "timeout") return "Live FSA search timed out. Showing cached results only — try again shortly.";
  if (state.last_result === "error") return "Live FSA search is unavailable right now. Showing cached results only.";
  return null;
}

export function PlanTodayPanel() {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [locating, setLocating] = useState(false);
  const [selected, setSelected] = useState({});
  const [drawer, setDrawer] = useState(null);
  // ad-hoc controls
  const [adHoc, setAdHoc] = useState(false);
  const [duration, setDuration] = useState(120);
  const [custom, setCustom] = useState(false);
  const [returnToStart, setReturnToStart] = useState(true);
  const [postcode, setPostcode] = useState("");
  const [showFunnel, setShowFunnel] = useState(false);
  const { user } = useAuth() || {};
  const canRefresh = ["admin", "super_admin"].includes(user?.role);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshResult, setRefreshResult] = useState(null);

  const refreshArea = async () => {
    const a = plan?.anchor || plan?.anchor_location || {};
    const radius = Math.min(15, plan?.diagnostics?.candidate_funnel?.final_radius_km || 3);
    let body = null;
    if (a.lat != null && a.lng != null) body = { latitude: a.lat, longitude: a.lng, radius_km: radius };
    else if (postcode.trim()) body = { postcode: postcode.trim(), radius_km: radius };
    if (!body) { toast("Enter a postcode for this area, or use Refresh area venues on Monitored Areas."); return; }
    if (refreshing) return;
    setRefreshing(true);
    const out = await refreshVenues(body);
    setRefreshing(false);
    if (!out) return;
    setRefreshResult(out);
    if (plan.plan_source === "normal") runNormal();
    else toast("Venues refreshed — run your plan again to use them.");
  };

  // Normal mode plan (today's availability window).
  const runNormal = async () => {
    setLoading(true);
    try {
      const { data } = await planTodayApi.compute();
      setPlan(data); setSelected({});
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setLoading(false); }
  };

  // Ad-hoc plan. `coords` is a one-shot local value — never stored in state, discarded after submit.
  const submitAdHoc = async ({ coords, pc }) => {
    const mins = Math.max(30, Math.min(600, parseInt(duration, 10) || 120));
    const body = { mode: "ad_hoc_current_location", duration_minutes: mins, return_to_start: returnToStart };
    if (coords) body.current_location = { latitude: coords.latitude, longitude: coords.longitude };
    else if (pc) body.manual_postcode = pc;
    setLoading(true);
    try {
      const { data } = await planTodayApi.compute(body);
      setPlan(data); setSelected({});
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setLoading(false); }
    // `coords`/`pc` go out of scope here — no persistent client-side location tracking.
  };

  const useMyLocation = async () => {
    setLocating(true);
    try {
      // native -> CoreLocation (contextual permission); web -> navigator.geolocation
      const coords = await getCurrentPosition();
      setLocating(false);
      submitAdHoc({ coords });
    } catch {
      setLocating(false);
      toast.error("Couldn't read your location. Enter a postcode instead.");
    }
  };

  const submitPostcode = () => {
    const pc = postcode.trim();
    if (!pc) { toast.error("Enter a UK postcode."); return; }
    submitAdHoc({ pc });
  };

  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const remove = async (id) => {
    if (!plan) return;
    try { const { data } = await tripSuggestionsApi.removeCandidate(plan.id, id); setPlan(data); }
    catch (e) { toast.error(apiErrorMessage(e)); }
  };
  const chosen = Object.keys(selected).filter((k) => selected[k]);
  const approve = async () => {
    try {
      const { data } = await tripSuggestionsApi.approve(plan.id, { selected_candidate_ids: chosen });
      toast.success(`Approved — ${data.visits_created} visit(s) created`);
      setPlan(null); setSelected({});
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  const cap = plan?.capacity || {};
  const pickDuration = (m) => { setCustom(false); setDuration(m); };

  return (
    <div className="mt-5 rounded-xl border border-slate-200 bg-white p-4" data-testid="plan-today-panel">
      <div className="flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 font-heading text-sm font-bold text-slate-800"><Route className="h-4 w-4 text-teal-600" /> Plan Today</h2>
        {!adHoc && (
          <button data-testid="plan-today-btn" onClick={runNormal} disabled={loading}
            className="inline-flex min-h-[44px] items-center gap-2 rounded-lg bg-teal-700 px-3 text-sm font-bold text-white hover:bg-teal-800 disabled:opacity-50">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Route className="h-4 w-4" />} {plan ? "Replan" : "Plan my day"}
          </button>
        )}
      </div>
      <p className="mt-1 text-xs text-slate-400">Build a sequenced field day from your CRM leads and nearby FSA prospects. Nothing is created until you approve.</p>

      {/* Ad-hoc mode toggle */}
      <label data-testid="plan-adhoc-toggle" htmlFor="plan-adhoc-check" style={{ touchAction: "manipulation" }}
        className="mt-3 flex min-h-[44px] cursor-pointer items-center gap-2 rounded-lg border border-slate-200 bg-slate-50/60 px-3">
        <input id="plan-adhoc-check" type="checkbox" checked={adHoc} data-testid="plan-adhoc-check"
          onChange={(e) => setAdHoc(e.target.checked)} className="h-5 w-5 accent-teal-700" />
        <span className="flex items-center gap-1.5 text-sm font-semibold text-slate-700"><LocateFixed className="h-4 w-4 text-teal-600" /> Plan from where I am now</span>
      </label>

      {adHoc && (
        <div className="mt-3 rounded-lg border border-teal-100 bg-teal-50/40 p-3" data-testid="plan-adhoc-controls">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">How long have you got?</p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {DURATIONS.map((d) => (
              <button key={d.m} data-testid={`plan-duration-${d.m}`} onClick={() => pickDuration(d.m)}
                style={{ touchAction: "manipulation" }}
                className={`inline-flex min-h-[44px] items-center rounded-full border px-4 text-sm font-semibold ${!custom && duration === d.m ? "border-teal-500 bg-teal-600 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-teal-300"}`}>
                {d.l}
              </button>
            ))}
            <button data-testid="plan-duration-custom" onClick={() => setCustom(true)}
              style={{ touchAction: "manipulation" }}
              className={`inline-flex min-h-[44px] items-center rounded-full border px-4 text-sm font-semibold ${custom ? "border-teal-500 bg-teal-600 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-teal-300"}`}>
              Custom
            </button>
          </div>
          {custom && (
            <div className="mt-2 flex items-center gap-2">
              <input data-testid="plan-duration-custom-input" type="number" min={30} max={600} value={duration}
                onChange={(e) => setDuration(e.target.value)}
                className="min-h-[44px] w-24 rounded-lg border border-slate-200 px-3 text-sm" />
              <span className="text-xs text-slate-500">minutes (30–600)</span>
            </div>
          )}

          <label data-testid="plan-return-toggle" htmlFor="plan-return-check" style={{ touchAction: "manipulation" }}
            className="mt-3 flex min-h-[44px] cursor-pointer items-center gap-2">
            <input id="plan-return-check" type="checkbox" checked={returnToStart} data-testid="plan-return-check"
              onChange={(e) => setReturnToStart(e.target.checked)} className="h-5 w-5 accent-teal-700" />
            <span className="flex items-center gap-1.5 text-sm text-slate-700"><RotateCcw className="h-3.5 w-3.5 text-slate-400" /> Leave time to get back to where I started</span>
          </label>

          <button data-testid="plan-use-location-btn" onClick={useMyLocation} disabled={loading || locating}
            className="mt-3 inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-lg bg-teal-700 px-3 text-sm font-bold text-white hover:bg-teal-800 disabled:opacity-50">
            {locating || loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <LocateFixed className="h-4 w-4" />} Use my current location
          </button>

          <div className="mt-3">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Or enter a postcode</p>
            <div className="mt-1.5 flex gap-2">
              <input data-testid="plan-postcode-input" value={postcode} onChange={(e) => setPostcode(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && submitPostcode()}
                placeholder="e.g. EC1A 1AA"
                className="min-h-[44px] flex-1 rounded-lg border border-slate-200 px-3 text-sm uppercase placeholder:normal-case placeholder:text-slate-300" />
              <button data-testid="plan-postcode-submit" onClick={submitPostcode} disabled={loading}
                className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-teal-200 bg-white px-3 text-sm font-bold text-teal-700 hover:bg-teal-50 disabled:opacity-50">
                <Route className="h-4 w-4" /> Plan
              </button>
            </div>
          </div>

          <p data-testid="plan-privacy-note" className="mt-3 flex items-start gap-1.5 text-[11px] leading-relaxed text-slate-500">
            <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
            Your location is used once for this plan only — it isn't saved to your profile, stored on this device, or tracked in the background.
          </p>
        </div>
      )}

      {plan && (
        <>
          <div className="mt-3 flex flex-wrap gap-1.5" data-testid="plan-today-summary">
            {plan.field_window?.start_at && plan.field_window?.end_at ? (
              <span className={`${CHIP} border-slate-200 bg-slate-50 text-slate-600`}><Clock className="mr-1 h-3 w-3" /> {fmt(plan.field_window?.start_at)}–{fmt(plan.field_window?.end_at)}</span>
            ) : (
              <span className={`${CHIP} border-slate-200 bg-slate-50 text-slate-600`}><Clock className="mr-1 h-3 w-3" /> {cap.available_min}min window</span>
            )}
            <span className={`${CHIP} border-teal-200 bg-teal-50 text-teal-700`}>{plan.anchor?.label || plan.anchor_location?.label || "Start"}</span>
            {plan.plan_source === "current_location_ad_hoc" && <span className={`${CHIP} border-teal-200 bg-teal-50 text-teal-700`} data-testid="plan-adhoc-badge">Ad-hoc</span>}
            <span className={`${CHIP} border-slate-200 bg-slate-50 text-slate-600`}>CRM {plan.crm_candidate_count}</span>
            <span className={`${CHIP} border-amber-200 bg-amber-50 text-amber-700`}>External {plan.external_candidate_count}</span>
            <span className={`${CHIP} border-slate-200 bg-slate-50 text-slate-600`}>{plan.candidates.length} stops</span>
            <span className={`${CHIP} border-slate-200 bg-slate-50 text-slate-600`}><Car className="mr-1 h-3 w-3" /> {cap.drive_min}min drive</span>
            <span className={`${CHIP} border-slate-200 bg-slate-50 text-slate-600`}>{cap.visit_min}min visits</span>
            <span className={`${CHIP} border-emerald-200 bg-emerald-50 text-emerald-700`}>{cap.remaining_min}min spare</span>
            {plan.discovery_used && <span className={`${CHIP} border-amber-200 bg-amber-50 text-amber-700`}>FSA: {plan.discovery_source}</span>}
          </div>

          {plan.candidates.length < 3 && fsaBannerText(plan.diagnostics?.live_fsa_state) && (
            <div data-testid="plan-fsa-banner" className="mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] leading-relaxed text-amber-800">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>
                {fsaBannerText(plan.diagnostics?.live_fsa_state)}
                {plan.diagnostics?.live_fsa_state?.rate_limit_resets_at && (
                  <> Resets at {fmt(plan.diagnostics.live_fsa_state.rate_limit_resets_at)}.</>
                )}
              </span>
            </div>
          )}

          {plan.candidates.length === 0 ? (
            <p data-testid="plan-today-empty" className="mt-3 rounded-lg border border-dashed border-slate-200 bg-slate-50/60 px-3 py-3 text-xs text-slate-400">No workable stops for this window.</p>
          ) : (
            <div className="mt-3 space-y-2">
              {plan.candidates.map((c) => (
                <div key={c.id} data-testid={`plan-stop-${c.id}`}
                  className={`rounded-lg border p-2.5 ${selected[c.id] ? "border-teal-400 bg-teal-50/40" : "border-slate-200 bg-white"}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex min-w-0 items-start gap-1">
                      <label data-selection-control htmlFor={`plan-select-${c.id}`} onClick={(e) => e.stopPropagation()}
                        style={{ touchAction: "manipulation" }}
                        className="relative -ml-1 flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-md focus-within:ring-2 focus-within:ring-teal-500">
                        <input id={`plan-select-${c.id}`} type="checkbox" data-testid={`plan-select-${c.id}`}
                          checked={!!selected[c.id]} onChange={(e) => { e.stopPropagation(); toggle(c.id); }}
                          onClick={(e) => e.stopPropagation()} className="h-5 w-5 accent-teal-700" />
                      </label>
                      <span className="min-w-0 pt-2 cursor-pointer" data-testid={`plan-body-${c.id}`}
                        onClick={() => c.is_external && c.external_id && setDrawer(c)}>
                        <span className="flex items-center gap-1.5 text-sm font-semibold text-slate-800">
                          {c.is_external ? <Globe className="h-3.5 w-3.5 text-amber-600" /> : <Building2 className="h-3.5 w-3.5 text-teal-600" />}
                          {c.name || "Prospect"}
                        </span>
                        <span className="mt-1 block">
                          <span className={`${CHIP} ${c.is_external ? "border-amber-200 bg-amber-50 text-amber-700" : "border-teal-200 bg-teal-50 text-teal-700"}`}>{c.is_external ? "External" : "CRM"}</span>
                          {c.fhrs_rating ? <span className={`${CHIP} ml-1 border-emerald-200 bg-emerald-50 text-emerald-700`}>FHRS {c.fhrs_rating}</span>
                            : <span className={`${CHIP} ml-1 border-slate-200 bg-slate-50 text-slate-400`}>No hygiene rating</span>}
                          {c.best_fit && <span className={`${CHIP} ml-1 border-teal-200 bg-teal-50 text-teal-700`}>{c.best_fit}</span>}
                          <span className={`${CHIP} ml-1 border-indigo-200 bg-indigo-50 text-indigo-700`}>{c.channel || "VISIT"}</span>
                          {c.channel_hint && <span data-testid={`plan-channel-hint-${c.id}`} className={`${CHIP} ml-1 border-slate-200 bg-slate-50 text-slate-400`}>better by {c.channel_hint}</span>}
                          <span className={`${CHIP} ml-1 border-slate-200 bg-slate-50 text-slate-600`}><MapPin className="mr-1 h-3 w-3" />{c.distance_km}km · {c.estimated_travel_min}min</span>
                        </span>
                        {(c.reasons || []).slice(0, 2).map((r, i) => <span key={i} className="mt-1 block text-[11px] text-slate-500">• {r}</span>)}
                      </span>
                    </div>
                    <button data-testid={`plan-remove-${c.id}`} onClick={() => remove(c.id)} className="shrink-0 rounded p-1 text-slate-300 hover:text-rose-500"><X className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
              <button data-testid="plan-approve-btn" onClick={approve} disabled={chosen.length === 0}
                className="inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-lg bg-teal-700 px-3 text-sm font-bold text-white hover:bg-teal-800 disabled:opacity-40">
                <CheckCircle2 className="h-4 w-4" /> Approve {chosen.length} selected
              </button>
            </div>
          )}

          {plan.diagnostics?.candidate_funnel && (
            <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50/60">
              <button data-testid="plan-why-toggle" onClick={() => setShowFunnel((v) => !v)}
                style={{ touchAction: "manipulation" }}
                className="flex min-h-[44px] w-full items-center justify-between gap-2 px-3 text-left text-xs font-bold text-slate-600 hover:text-slate-800">
                <span className="flex items-center gap-1.5"><Search className="h-3.5 w-3.5 text-slate-400" /> Why these results?</span>
                {showFunnel ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </button>
              {showFunnel && (
                <ul data-testid="plan-why-body" className="space-y-1 px-3 pb-3 pt-0">
                  {funnelLines(plan.diagnostics.candidate_funnel).map((l, i) => (
                    <li key={i} className="flex gap-1.5 text-[11px] leading-relaxed text-slate-500">
                      <span className="text-slate-300">•</span> {l}
                    </li>
                  ))}
                  {canRefresh && (
                    <li className="pt-1">
                      <button type="button" data-testid="plan-refresh-venues" onClick={refreshArea} disabled={refreshing}
                        className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-teal-200 bg-white px-3 text-xs font-semibold text-teal-700 disabled:opacity-50">
                        <RotateCcw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
                        {refreshing ? "Refreshing venues…" : "Refresh venues for this area"}
                      </button>
                      <RefreshResult result={refreshResult} />
                    </li>
                  )}
                </ul>
              )}
            </div>
          )}
        </>
      )}
      <ProspectDrawer prospect={drawer} onClose={() => setDrawer(null)}
        selected={drawer ? !!selected[drawer.id] : false}
        onToggleSelect={() => drawer && toggle(drawer.id)}
        onConverted={() => setDrawer(null)}
        onDismiss={() => { if (drawer) { remove(drawer.id); setDrawer(null); } }} />
    </div>
  );
}
